// Copyright 2026, Imprivata, Inc.  All rights reserved.

import type { SubRule } from '@api/types';
import type { Condition } from '../types';

export const EXPR_TYPE_TRUE = 'true';
const EXPR_TYPE_CONDITION = 'expr';
const PART_TYPE_VALUE_COMPARISON = 'value-comparison';
const PART_TYPE_USER_HAS_ROLE = 'user-has-role';
const ADMIN_ROLE = 'admin';
// PROVISIONAL pending the rules service contract.
const RISK_OPERAND = 'risk-outcome';
const RISK_OPERATOR = '==';
// PROVISIONAL pending the rules service contract.
const ACTION_FIELD = 'value' satisfies keyof SubRule;

type RecognisedCondition = { condition: Condition; conditionValue?: string };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const readAction = (sub: SubRule): string | undefined => sub[ACTION_FIELD];

export const writeAction = (sub: SubRule, action: string | undefined): SubRule =>
  action === undefined ? sub : { ...sub, [ACTION_FIELD]: action };

const EXPR_BY_CONDITION = {
  'role-is-administrator': () => ({
    parts: [{ partType: PART_TYPE_USER_HAS_ROLE, userHasRolePart: { roleType: ADMIN_ROLE } }],
  }),
  'risk-level': (value: string) => ({
    parts: [
      {
        partType: PART_TYPE_VALUE_COMPARISON,
        valueComparisonPart: { operand: RISK_OPERAND, operator: RISK_OPERATOR, value },
      },
    ],
  }),
} satisfies Record<Condition, (value: string) => unknown>;

export const synthesise = (condition: Condition, conditionValue = ''): SubRule => ({
  exprType: EXPR_TYPE_CONDITION,
  expr: EXPR_BY_CONDITION[condition](conditionValue),
});

const recognisePart = (part: unknown): RecognisedCondition | null => {
  if (!isRecord(part)) { return null; }
  const { partType, userHasRolePart: role, valueComparisonPart: comparison } = part;

  if (partType === PART_TYPE_USER_HAS_ROLE && isRecord(role) && role.roleType === ADMIN_ROLE) {
    return { condition: 'role-is-administrator' };
  }
  if (
    partType === PART_TYPE_VALUE_COMPARISON &&
    isRecord(comparison) &&
    comparison.operand === RISK_OPERAND &&
    comparison.operator === RISK_OPERATOR &&
    typeof comparison.value === 'string'
  ) {
    return { condition: 'risk-level', conditionValue: comparison.value };
  }
  return null;
};

// An allowlist over single-part expressions; anything else is carried opaquely.
export const recogniseCondition = ({ exprType, expr }: SubRule): RecognisedCondition | null =>
  exprType === EXPR_TYPE_CONDITION && isRecord(expr) && Array.isArray(expr.parts) && expr.parts.length === 1
    ? recognisePart(expr.parts[0])
    : null;
